import * as React from 'react';
import { lastStudioProposal, proposeStudioPhrase, type StudioProposalSummary } from '@/uptk/modules/module-studio/commands/commandCatalog';
import StudioPlanChoiceCard from '@/uptk/modules/module-studio/commands/StudioPlanChoiceCard';
import { getStudioPlanWindow, subscribeStudioPlanWindow } from '@/uptk/modules/module-studio/commands/studioPlanWindow';
import { StudioCommandSuggestions } from '@/uptk/modules/module-studio/commands/StudioCommands';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '@clerk/clerk-react';
import { EmptyState } from '@/design-system/ds-0/components/States';
import { IllustrationAssistant } from '@/design-system/ds-0/illustrations';
import { getPreferredAccessToken } from '@/uptk/core/api/clerkTokenProvider';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';
import { subscribeAuthContextState } from '@/uptk/core/api/auth_context_store';
import { subscribeTenantOverrideState } from '@/uptk/core/api/tenant_override_store';
import {
  getFormActions,
  subscribeMobileFormShell,
} from '@/uptk/core/utils/mobile_form_shell_store';
import { matchCommand } from '@/uptk/core/utils/mobileCommandMatcher';
import {
  CopilotWriteConfirmationCard,
  CopilotWriteResultLine,
  type WriteConfirmationDecision,
} from './CopilotWriteConfirmationCard';
import { CopilotActivityLine } from './CopilotActivityLine';
import { CopilotSummaryLine } from './CopilotSummaryLine';
import {
  activityVerb,
  applyActivity,
  buildEntrySummary,
  formatTokens,
  isBudgetNearlyExhausted,
  localeOf,
  parseActivityEvent,
  parseUsageEvent,
  startActivity,
  type ActivityState,
  type EntrySummary,
  type StreamUsage,
} from './copilotActivity';
import { copilotErrorText, parseCopilotErrorCode } from './copilotErrorMessages';
import { COPILOT_OPEN_EVENT, promptOf } from './copilotBridge';
import VoiceButton from '@/uptk/core/components/voice/VoiceButton';
import VoiceDrawerSection from '@/uptk/core/components/voice/VoiceDrawerSection';
import { isPublicPage } from '@/uptk/core/components/header/headerContext';
import {
  buildConfirmWrite,
  buildStreamBody,
  parseWriteConfirmation,
  parseWriteResult,
  type ConfirmWriteBody,
  type WriteConfirmation,
  type WriteResult,
} from './copilotWriteConfirmation';

/**
 * Geode Copilot drawer (UPT-1437 Phase 0).
 *
 * Floating launcher + right-side chat panel mounted globally from ClientProviders.
 * Renders only inside authenticated portal shells (/portal, /adminportal, /make).
 *
 * Streams POST /api/v1/ai/copilot/stream (SSE) and handles events:
 * conversation | activity | usage | tool_call | tool_result | tool_error | ui_action | message |
 * suggestions | write_confirmation | write_result | done | error.
 * `ui_action {type: "navigate"}` drives the router — the copilot PILOTS the app.
 *
 * Activity and tokens (UPT-2041 lot 1): `activity` events feed an EPHEMERAL activity line shown while the
 * copilot works (never an entry of the conversation, never persisted); `tool_call` / `tool_result` /
 * `tool_error` still arrive but are no longer shown. `usage` (after `message`, before `done`) feeds the
 * summary line under the answer and the drawer-footer token count of the conversation. The Stop button
 * aborts the fetch (client side: the server finishes on its own).
 *
 * Writes (UPT-2016 lot 2): the copilot never writes by itself. When the model asks
 * for a write the server writes NOTHING and emits `write_confirmation`; the drawer
 * shows a card ("The assistant proposes: ...") with Confirm / Cancel. Only the click
 * sends a NEW stream request carrying `confirmWrite: { requestId, confirmed }`; the
 * server answers with `write_result` (EXECUTED | FAILED | CANCELLED | EXPIRED).
 */

// Retour d'Olivier du 20.10 : Géode (copilote et voix) sur toute page connectée — portails, /studio, pages racine comme /profile —
// sauf les pages publiques. Même règle inversée que l'en-tête (header/headerContext.ts).
const isAppRoute = (pathname: string): boolean => !isPublicPage(pathname);

interface ChatEntry {
  role: 'user' | 'assistant' | 'error' | 'write-confirmation' | 'write-result';
  /** Identifiant local des entrées d'un tour de réponse : le bilan (`usage`) arrive après elles. */
  id?: number;
  content: string;
  /** Bilan de la réponse (étapes, durée, jetons), en mémoire de session seulement. */
  summary?: EntrySummary;
  /** role 'write-confirmation' : la demande d'écriture à confirmer. */
  confirmation?: WriteConfirmation;
  /** role 'write-confirmation' : la réponse déjà donnée par l'utilisateur (null = en attente du clic). */
  decision?: WriteConfirmationDecision | null;
  /** role 'write-result' : l'issue de la demande. */
  result?: WriteResult;
}

interface UiAction {
  type: string;
  path?: string | null;
  label?: string | null;
}

/**
 * Recursively collects {label, path} pairs from the runtime navigation manifest.
 * Shape-tolerant on purpose: any object carrying a string starting with '/' under
 * a path-ish key is treated as a navigable route.
 */
const collectNavigationPaths = (node: unknown, acc: { label: string; path: string }[]): void => {
  if (acc.length >= 80 || node === null || typeof node !== 'object') {
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((child) => collectNavigationPaths(child, acc));
    return;
  }
  const record = node as Record<string, unknown>;
  const pathValue = ['routePath', 'path', 'route', 'url']
    .map((key) => record[key])
    .find((value) => typeof value === 'string' && value.startsWith('/')) as string | undefined;
  if (pathValue && !acc.some((entry) => entry.path === pathValue)) {
    const labelValue = ['label', 'name', 'title', 'key']
      .map((key) => record[key])
      .find((value) => typeof value === 'string' && value.length > 0) as string | undefined;
    acc.push({ label: labelValue ?? pathValue, path: pathValue });
  }
  Object.values(record).forEach((child) => collectNavigationPaths(child, acc));
};

/**
 * The runtime manifest carries TEMPLATE paths (e.g. /:workspaceId/:year/finance/budgets).
 * The strict-navigation rule requires verbatim usable paths, so resolve the
 * placeholders with the current scope, prepend the portal prefix when missing,
 * and drop anything still carrying a placeholder.
 */
const resolveNavigationTemplates = (
  items: { label: string; path: string }[],
  portalType: string,
  workspaceId: string,
  year: string,
): { label: string; path: string }[] => {
  const resolved = items.map(({ label, path }) => {
    let target = path
      .replace(/:workspaceId/g, workspaceId)
      .replace(/:year/g, year);
    if (!/^\/(portal|adminportal|make)(\/|$)/.test(target)) {
      target = `/${portalType}${target.startsWith('/') ? '' : '/'}${target}`;
    }
    return { label, path: target };
  }).filter(({ path }) => !path.includes(':'));
  return resolved.filter((entry, index) =>
    resolved.findIndex((other) => other.path === entry.path) === index);
};

const parseSseBlocks = (buffer: string): { blocks: { event: string; data: string }[]; rest: string } => {
  const blocks: { event: string; data: string }[] = [];
  let working = buffer;
  let sep = working.indexOf('\n\n');
  while (sep >= 0) {
    const rawBlock = working.slice(0, sep);
    working = working.slice(sep + 2);
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of rawBlock.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length > 0) {
      blocks.push({ event, data: dataLines.join('\n') });
    }
    sep = working.indexOf('\n\n');
  }
  return { blocks, rest: working };
};

const FAB_STORAGE_KEY = 'copilot.fab.bottom';
const FAB_DEFAULT_BOTTOM = 20;
const FAB_MIN_BOTTOM = 8;
const FAB_SIZE = 48;
const FAB_DRAG_THRESHOLD = 5;

/** E2E-FIX1 (GAP-12) : le tiroir confirme ce que la phrase a proposé au Studio, plan compris (étapes numérotées). */
function studioProposalText(proposal: StudioProposalSummary, format: (id: string, values?: Record<string, number>) => string): string {
  if (proposal.kind === 'unrecognized') return format('moduleStudio.copilot.phraseUnrecognized');
  if (proposal.kind === 'version') return format('moduleStudio.copilot.nextVersion', { version: proposal.nextVersion ?? 0 });
  const head = format(proposal.kind === 'plan' ? 'moduleStudio.copilot.planProposed' : 'moduleStudio.copilot.commandsProposed', { count: proposal.count });
  return proposal.kind === 'plan' ? [head, ...proposal.labelIds.map((id, index) => (index + 1) + '. ' + format(id))].join('\n') : head;
}

export function CopilotDrawer() {
  const intl = useIntl();
  // GAP-12 : la fenêtre « Commandes du Studio » (z-index modal) reste au-dessus du tiroir (z-[70]) ; le tiroir le sait et le dit.
  const studioWindow = React.useSyncExternalStore(subscribeStudioPlanWindow, getStudioPlanWindow, getStudioPlanWindow);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  const [open, setOpen] = React.useState(false);
  // UAT 14.09 (Olivier) : le bouton flottant masquait des actions en bas à droite — il se déplace
  // désormais le long du bord droit (glisser à la souris ou au doigt, position mémorisée par navigateur).
  const [fabBottom, setFabBottom] = React.useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(FAB_STORAGE_KEY);
      const value = raw == null ? NaN : Number(raw);
      return Number.isFinite(value) ? value : FAB_DEFAULT_BOTTOM;
    } catch {
      return FAB_DEFAULT_BOTTOM;
    }
  });
  const fabDrag = React.useRef<{ pointerId: number; startY: number; startBottom: number; moved: boolean } | null>(null);
  const clampFabBottom = React.useCallback((value: number) => {
    const max = Math.max(FAB_MIN_BOTTOM, window.innerHeight - FAB_SIZE - FAB_MIN_BOTTOM);
    return Math.min(max, Math.max(FAB_MIN_BOTTOM, Math.round(value)));
  }, []);
  const handleFabPointerDown = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    fabDrag.current = { pointerId: e.pointerId, startY: e.clientY, startBottom: fabBottom, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [fabBottom]);
  const handleFabPointerMove = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = fabDrag.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const delta = drag.startY - e.clientY;
    if (!drag.moved && Math.abs(delta) < FAB_DRAG_THRESHOLD) return;
    drag.moved = true;
    setFabBottom(clampFabBottom(drag.startBottom + delta));
  }, [clampFabBottom]);
  const handleFabPointerUp = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = fabDrag.current;
    fabDrag.current = null;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    if (drag.moved) {
      try { window.localStorage.setItem(FAB_STORAGE_KEY, String(clampFabBottom(drag.startBottom + (drag.startY - e.clientY)))); } catch { /* storage unavailable */ }
      return;
    }
    setOpen(true);
  }, [clampFabBottom]);
  const handleFabPointerCancel = React.useCallback(() => {
    fabDrag.current = null;
  }, []);
  const [entries, setEntries] = React.useState<ChatEntry[]>([]);
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [input, setInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // Activité du tour en cours : éphémère, jamais ajoutée aux `entries`, jamais persistée.
  const [activity, setActivity] = React.useState<ActivityState | null>(null);
  // Cumul de jetons de la conversation (dernier `usage`) et budget du mois, pour le pied de tiroir.
  const [conversationTokens, setConversationTokens] = React.useState<number | null>(null);
  const [budgetNearlyExhausted, setBudgetNearlyExhausted] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const entryIdRef = React.useRef(0);
  const conversationIdRef = React.useRef<string | null>(null);
  // Demandes d'écriture déjà répondues : un double clic ne renvoie jamais deux requêtes.
  const answeredWritesRef = React.useRef<Set<string>>(new Set());
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const navCacheRef = React.useRef<{ key: string; items: { label: string; path: string }[] } | null>(null);

  const onPortalRoute = isAppRoute(location.pathname);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, open, activity]);

  // Conversations are scoped to (tenant, user) server-side; the drawer must not
  // carry messages across a tenant switch (UPT-1437 board feedback 05/08).
  // Workspace/year changes only refresh the per-message pageContext, by design.
  const tenantIdRef = React.useRef<string>(resolveSaasContext({}).tenantId ?? '');

  // Abandonne le flux en cours SANS rien écrire dans la conversation (changement de locataire, démontage) :
  // contrairement à Stop, il n'y a ni « Réponse interrompue » ni bilan partiel à montrer. `abortRef` est vidé
  // AVANT l'abandon : c'est ce qui distingue, dans `send`, un tour abandonné d'un Stop de l'utilisateur.
  const discardStream = React.useCallback(() => {
    const controller = abortRef.current;
    abortRef.current = null;
    controller?.abort();
  }, []);

  // Le démontage coupe le flux : sinon la lecture continue (et réécrit l'état) après la fermeture du composant.
  React.useEffect(() => () => discardStream(), [discardStream]);

  React.useEffect(() => {
    const resetIfTenantChanged = () => {
      const currentTenantId = resolveSaasContext({}).tenantId ?? '';
      if (currentTenantId !== tenantIdRef.current) {
        tenantIdRef.current = currentTenantId;
        // Le flux du locataire précédent ne doit ni survivre ni écrire dans la conversation remise à zéro,
        // ni rétablir un cumul de jetons (`usage`) qui n'est plus le sien : on le coupe AVANT la remise à zéro.
        discardStream();
        setBusy(false);
        setActivity(null);
        conversationIdRef.current = null;
        setEntries([]);
        setSuggestions([]);
        setInput('');
        setConversationTokens(null);
        setBudgetNearlyExhausted(false);
      }
    };
    const unsubscribeAuthContext = subscribeAuthContextState(resetIfTenantChanged);
    const unsubscribeTenantOverride = subscribeTenantOverrideState(resetIfTenantChanged);
    return () => {
      unsubscribeAuthContext();
      unsubscribeTenantOverride();
    };
  }, [discardStream]);

  const applyUiAction = React.useCallback((action: UiAction) => {
    if (action.type === 'navigate' && action.path && action.path.startsWith('/')) {
      void navigate(action.path);
    }
  }, [navigate]);

  // UPT-1616 batch-3 (Olivier): ONE Copilot button everywhere — when a form
  // is on screen, its actions are registered in the shell store and the
  // drawer executes matching commands locally before involving the backend.
  const formActions = React.useSyncExternalStore(
    subscribeMobileFormShell,
    getFormActions,
    () => null,
  );
  const hasFormActions = (formActions?.targets.length ?? 0) > 0;

  // `confirmWrite` n'est fourni QUE par le clic Confirmer / Annuler d'une carte d'écriture :
  // la requête de flux qui porte le clic est la seule à pouvoir écrire.
  const send = React.useCallback(async (text: string, confirmWrite?: ConfirmWriteBody) => {
    const message = text.trim();
    if (!message || busy) {
      return;
    }

    if (!confirmWrite && proposeStudioPhrase(message)) {
      setInput('');
      const proposal = lastStudioProposal();
      if (proposal) setEntries(prev => [...prev, { role: 'user', content: message },
        { role: 'assistant', content: studioProposalText(proposal, (id, values) => intl.formatMessage({ id }, values)) }]);
      return;
    }
    if (location.pathname.includes('/module-studio/dossiers/')) {
      setEntries(prev => [...prev, { role: 'assistant', content: intl.formatMessage({ id: 'moduleStudio.command.loading' }) }]);
      return;
    }

    // Form-command fast path: deterministic local match, no backend round-trip.
    // (Jamais pour la réponse à une carte d'écriture : ce n'est pas une commande de formulaire.)
    const actions = confirmWrite ? null : getFormActions();
    if (actions) {
      const matched = matchCommand(message, actions.targets);
      if (matched) {
        setInput('');
        setEntries((prev) => [
          ...prev,
          { role: 'user', content: message },
          {
            role: 'assistant',
            content: intl.formatMessage({ id: 'core.mobile.copilot.executed' }, { action: matched.label }),
          },
        ]);
        actions.execute(matched.key);
        return;
      }
    }

    setBusy(true);
    setSuggestions([]);
    setInput('');
    // La réponse à une carte (Confirmer / Annuler) s'affiche sur la carte, pas comme un message tapé.
    if (!confirmWrite) {
      setEntries((prev) => [...prev, { role: 'user', content: message }]);
    }

    // UPT-2041 : l'état du tour (activité, usage, entrée de réponse) vit dans cet objet local, source de vérité
    // pendant la lecture du flux ; React ne reçoit que des copies (l'activité n'entre jamais dans `entries`).
    const controller = new AbortController();
    abortRef.current = controller;
    const turn: { startedAt: number; activity: ActivityState; usage: StreamUsage | null; durableId: number | null } = {
      startedAt: Date.now(),
      activity: startActivity(Date.now()),
      usage: null,
      durableId: null,
    };
    setActivity(turn.activity);
    const turnSummary = (interrupted = false): EntrySummary | undefined =>
      buildEntrySummary(turn.activity.items, turn.usage, Date.now() - turn.startedAt, interrupted);
    // Une entrée durable (réponse, carte d'écriture, résultat d'écriture) porte le bilan du tour : un seul par
    // tour, sous la dernière entrée créée.
    const pushDurable = (entry: ChatEntry) => {
      entryIdRef.current += 1;
      const id = entryIdRef.current;
      const previousId = turn.durableId;
      turn.durableId = id;
      const summary = turnSummary();
      setEntries((prev) => [
        ...prev.map((existing) => {
          if (previousId !== null && existing.id === previousId) {
            const { summary: dropped, ...rest } = existing;
            void dropped;
            return rest;
          }
          return existing;
        }),
        { ...entry, id, ...(summary ? { summary } : {}) },
      ]);
    };
    const refreshSummary = () => {
      const id = turn.durableId;
      const summary = turnSummary();
      if (id === null || !summary) {
        return;
      }
      setEntries((prev) => prev.map((existing) => (existing.id === id ? { ...existing, summary } : existing)));
    };

    try {
      const token = await getPreferredAccessToken();
      // buildSaasHeaders emits each SaaS header under two casings (tenantid +
      // tenantId) for axios, but fetch() MERGES same-name headers into
      // "value, value", which the backend rejects as an invalid UUID.
      // Headers.set() with lowercased names dedupes them.
      const headers = new Headers();
      Object.entries(buildSaasHeaders(resolveSaasContext({}))).forEach(([name, value]) => {
        headers.set(name.toLowerCase(), value);
      });
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'text/event-stream');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Real navigation targets for the strict-navigation rule: the copilot may
      // only navigate to paths from this list (fetched once per tenant/portal).
      const saas = resolveSaasContext({});
      const portalType = location.pathname.startsWith('/adminportal') ? 'adminportal' : 'portal';
      const navKey = `${saas.tenantId}|${portalType}|${saas.workspaceId}`;
      if (!navCacheRef.current || navCacheRef.current.key !== navKey) {
        try {
          // Own headers for this JSON GET: reusing the stream headers sends
          // Accept: text/event-stream and the endpoint answers 406 → empty
          // navigation → the copilot refuses to navigate (board 06/08).
          const navHeaders = new Headers(headers);
          navHeaders.set('Accept', 'application/json');
          const navResponse = await fetch(
            `/api/v1/navigation/runtime?portalType=${portalType}&workspaceId=${saas.workspaceId || 'default'}`,
            { headers: navHeaders, signal: controller.signal });
          const navItems: { label: string; path: string }[] = [];
          if (navResponse.ok) {
            collectNavigationPaths(await navResponse.json(), navItems);
          }
          navCacheRef.current = {
            key: navKey,
            items: resolveNavigationTemplates(
              navItems,
              portalType,
              saas.workspaceId || 'default',
              String(new Date().getFullYear()),
            ),
          };
        } catch {
          if (controller.signal.aborted) {
            // Stop ou abandon du tour : ne jamais mettre en cache une navigation vide à cause d'un abandon.
            throw new DOMException('Aborted', 'AbortError');
          }
          navCacheRef.current = { key: navKey, items: [] };
        }
      }

      const response = await fetch('/api/v1/ai/copilot/stream', {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify(buildStreamBody({
          message,
          conversationId: conversationIdRef.current,
          pageContext: JSON.stringify({
            path: location.pathname,
            navigation: navCacheRef.current.items,
          }),
          confirmWrite,
        })),
      });
      if (!response.ok || !response.body) {
        // Réponse d'erreur HTTP : son corps JSON peut porter un `code` stable à traduire (jamais le message brut).
        const errorCode = parseCopilotErrorCode(await response.text().catch(() => ''));
        setEntries((prev) => [
          ...prev,
          { role: 'error', content: copilotErrorText((id) => intl.formatMessage({ id }), errorCode) },
        ]);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finished = false;
      while (!finished) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const { blocks, rest } = parseSseBlocks(buffer);
        buffer = rest;
        for (const block of blocks) {
          switch (block.event) {
            case 'conversation': {
              // Une autre conversation (le serveur en ouvre une nouvelle) : le cumul de jetons repart de zéro.
              const previousConversationId = conversationIdRef.current;
              conversationIdRef.current = block.data;
              if (previousConversationId !== null && previousConversationId !== block.data) {
                setConversationTokens(null);
              }
              break;
            }
            case 'activity': {
              const item = parseActivityEvent(block.data);
              if (item) {
                turn.activity = applyActivity(turn.activity, item);
                setActivity(turn.activity);
              }
              break;
            }
            case 'usage': {
              // Après `message` : on rattache le bilan à la réponse qui vient de tomber.
              const usage = parseUsageEvent(block.data);
              if (usage) {
                turn.usage = usage;
                setConversationTokens(usage.conversationTotalTokens);
                setBudgetNearlyExhausted(isBudgetNearlyExhausted(usage));
                refreshSummary();
              }
              break;
            }
            case 'tool_call':
            case 'tool_result':
            case 'tool_error':
              // Conservés par le serveur pour compatibilité (nom brut de l'outil) : jamais affichés.
              break;
            case 'ui_action': {
              try {
                applyUiAction(JSON.parse(block.data) as UiAction);
              } catch {
                // malformed action payloads are ignored by design
              }
              break;
            }
            case 'message':
              pushDurable({ role: 'assistant', content: block.data });
              break;
            case 'suggestions': {
              try {
                const parsed = JSON.parse(block.data) as string[];
                setSuggestions(Array.isArray(parsed) ? parsed.slice(0, 4) : []);
              } catch {
                // ignore malformed suggestion payloads
              }
              break;
            }
            case 'write_confirmation': {
              // Rien n'est écrit à ce stade : on montre la demande et on attend le clic.
              const confirmation = parseWriteConfirmation(block.data);
              if (confirmation) {
                pushDurable({ role: 'write-confirmation', content: confirmation.summary, confirmation, decision: null });
              } else {
                setEntries((prev) => [
                  ...prev,
                  { role: 'error', content: intl.formatMessage({ id: 'copilot.writeConfirmation.invalid' }) },
                ]);
              }
              break;
            }
            case 'write_result': {
              const result = parseWriteResult(block.data);
              if (result) {
                pushDurable({ role: 'write-result', content: result.status, result });
              }
              break;
            }
            case 'error':
              // `block.data` = JSON { code, message } : on traduit le code, le message anglais n'est pas affiché.
              setEntries((prev) => [
                ...prev,
                {
                  role: 'error',
                  content: copilotErrorText(
                    (id) => intl.formatMessage({ id }),
                    parseCopilotErrorCode(block.data),
                  ),
                },
              ]);
              finished = true;
              break;
            case 'done':
              finished = true;
              break;
            default:
              break;
          }
        }
      }
    } catch {
      if (controller.signal.aborted) {
        // Tour abandonné (locataire changé, composant démonté) : la conversation n'est plus la sienne.
        if (abortRef.current !== controller) {
          return;
        }
        // Stop : « Réponse interrompue » avec le bilan partiel (étapes vues, durée, jetons inconnus). Si la
        // réponse était déjà tombée, elle reste telle quelle.
        if (turn.durableId === null) {
          const summary = turnSummary(true);
          setEntries((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: intl.formatMessage({ id: 'copilot.activity.interrupted' }),
              ...(summary ? { summary } : {}),
            },
          ]);
        }
      } else {
        setEntries((prev) => [
          ...prev,
          { role: 'error', content: intl.formatMessage({ id: 'copilot.error.generic' }) },
        ]);
      }
    } finally {
      // Un tour abandonné ne touche plus à l'état : il appartient déjà au locataire suivant (ou au néant).
      const discarded = controller.signal.aborted && abortRef.current !== controller;
      if (!discarded) {
        refreshSummary();
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setActivity(null);
        setBusy(false);
      }
    }
  }, [busy, location.pathname, applyUiAction, intl]);

  // Stop (côté client) : coupe le fetch ; le serveur termine seul (l'annulation serveur est le lot 3).
  const stop = React.useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Clic sur Confirmer / Annuler d'une carte : une NOUVELLE requête de flux avec `confirmWrite`.
  // Une demande ne reçoit qu'une réponse ; si la requête échoue, l'utilisateur redemande à l'assistant
  // (jamais de rejeu automatique d'une écriture).
  const answerWrite = React.useCallback((requestId: string, confirmed: boolean) => {
    if (busy || answeredWritesRef.current.has(requestId)) {
      return;
    }
    answeredWritesRef.current.add(requestId);
    setEntries((prev) => prev.map((entry) =>
      entry.confirmation?.requestId === requestId
        ? { ...entry, decision: confirmed ? 'confirmed' : 'cancelled' }
        : entry));
    void send(
      intl.formatMessage({
        id: confirmed ? 'copilot.writeConfirmation.message.confirm' : 'copilot.writeConfirmation.message.cancel',
      }),
      buildConfirmWrite(requestId, confirmed),
    );
  }, [busy, intl, send]);

  // UPT-2126 A3 — pont d'ouverture (`openCopilot`, copilotBridge.ts) : une page du portail (l'accueil Géode) ouvre
  // le tiroir et, avec un `prompt`, envoie ce message comme si l'utilisateur l'avait tapé. Déclaré AVANT le
  // `return null` ci-dessous (règle des hooks). `send` et `busy` sont lus par référence : l'écouteur n'est pas
  // réabonné à chaque rendu, et un tour déjà en cours n'est jamais doublé.
  const sendRef = React.useRef(send);
  sendRef.current = send;
  const busyRef = React.useRef(busy);
  busyRef.current = busy;
  React.useEffect(() => {
    if (!isSignedIn || !onPortalRoute) return undefined;
    const onOpenRequest = (event: Event) => {
      setOpen(true);
      const prompt = promptOf(event);
      if (prompt && !busyRef.current) {
        void sendRef.current(prompt);
      }
    };
    window.addEventListener(COPILOT_OPEN_EVENT, onOpenRequest);
    return () => window.removeEventListener(COPILOT_OPEN_EVENT, onOpenRequest);
  }, [isSignedIn, onPortalRoute]);

  if (!isSignedIn || !onPortalRoute) {
    return null;
  }

  // Verbe courant annoncé aux lecteurs d'écran, par la région vocale ci-dessous (vide hors tour de réponse).
  const liveVerb = busy && activity
    ? activityVerb(activity, (id, values) => intl.formatMessage({ id }, values))
    : '';

  // Une entrée de réponse, de carte ou de résultat d'écriture, suivie de la ligne de bilan du tour s'il y en a une.
  const withSummary = (idx: number, entry: ChatEntry, node: React.ReactNode) =>
    entry.summary ? (
      <div key={idx} className="space-y-1">
        {node}
        <CopilotSummaryLine summary={entry.summary} />
      </div>
    ) : (
      <React.Fragment key={idx}>{node}</React.Fragment>
    );

  return (
    <>
      {!open ? (
        <button
          type="button"
          onPointerDown={handleFabPointerDown}
          onPointerMove={handleFabPointerMove}
          onPointerUp={handleFabPointerUp}
          onPointerCancel={handleFabPointerCancel}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
          aria-label={intl.formatMessage({ id: 'copilot.button.open' })}
          title={intl.formatMessage({ id: 'copilot.button.dragHint' })}
          style={{ bottom: fabBottom, touchAction: 'none' }}
          className="fixed right-5 z-[70] flex h-12 w-12 cursor-grab items-center justify-center rounded-full bg-portal-accent text-white shadow-lg transition-transform hover:scale-105 active:cursor-grabbing"
        >
          <i className="fa fa-magic" aria-hidden="true" />
        </button>
      ) : null}

      {open ? (
        <div data-studio-window={studioWindow.open ? 'front' : undefined} className="fixed inset-y-0 right-0 z-[70] flex w-full flex-col border-l border-portal-border bg-portal-bg text-portal-ink shadow-2xl sm:w-[380px]">
          <div className="flex items-center justify-between border-b border-portal-border px-4 py-3">
            <div>
              <h2 className="ui-title text-base font-semibold">
                {intl.formatMessage({ id: 'copilot.title' })}
              </h2>
              <p className="text-xs text-portal-ink-muted">
                {intl.formatMessage({ id: 'copilot.disclaimer' })}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* UPT-2000 V0 — micro vocal (auto-masqué si l'add-on est inactif
                  et le simulateur non activé) ; transcriptions en fin de fil. */}
              <VoiceButton
                context="copilot"
                pageContext={JSON.stringify({ path: location.pathname })}
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={intl.formatMessage({ id: 'copilot.button.close' })}
                className="flex h-8 w-8 items-center justify-center rounded text-portal-ink-muted hover:text-portal-ink"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Région vocale montée AVEC le tiroir, avant tout contenu : le premier verbe est ainsi annoncé (une
              région `role="status"` insérée déjà remplie ne l'est pas toujours). La ligne d'activité visible
              n'est plus une région vocale, elle ne double pas l'annonce. */}
          <div role="status" aria-live="polite" className="sr-only">
            {liveVerb}
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {entries.length === 0 ? (
              // Olivier (04.10) : l'état initial est un accueil, pas une impasse — le
              // champ de saisie en bas est l'action, donc pas de bouton ici.
              <EmptyState
                variant="first-use"
                illustration={<IllustrationAssistant />}
                titleAs="h3"
                title={intl.formatMessage({ id: 'core.emptyState.copilot.title' })}
                description={intl.formatMessage({ id: 'core.emptyState.copilot.description' })}
              />
            ) : null}
            {entries.map((entry, idx) => {
              if (entry.role === 'write-confirmation' && entry.confirmation) {
                return withSummary(
                  idx,
                  entry,
                  <CopilotWriteConfirmationCard
                    confirmation={entry.confirmation}
                    decision={entry.decision ?? null}
                    busy={busy}
                    onAnswer={answerWrite}
                  />,
                );
              }
              if (entry.role === 'write-result' && entry.result) {
                return withSummary(idx, entry, <CopilotWriteResultLine result={entry.result} />);
              }
              // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: CSS utility classes, not user copy
              let bubbleClass = 'mr-8 bg-portal-surface-muted border-portal-border';
              if (entry.role === 'user') {
                // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: CSS utility classes, not user copy
                bubbleClass = 'ml-8 bg-portal-accent/10 border-portal-accent/30';
              } else if (entry.role === 'error') {
                // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: CSS utility classes, not user copy
                bubbleClass = 'mr-8 border-danger-solid/40 bg-danger-solid/10';
              }
              return withSummary(
                idx,
                entry,
                <div className={`whitespace-pre-wrap rounded-lg border px-3 py-2 text-sm ${bubbleClass}`}>
                  {entry.content}
                </div>,
              );
            })}
            {busy && activity ? <CopilotActivityLine activity={activity} onStop={stop} /> : null}
            {/* UPT-2000 V0 — transcriptions et cartes vocales en fin de fil. */}
            <StudioPlanChoiceCard />
            <StudioCommandSuggestions nextOnly />
            <VoiceDrawerSection />
          </div>

          {suggestions.length > 0 ? (
            <div className="flex flex-wrap gap-2 px-4 pb-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => { void send(suggestion); }}
                  className="rounded-full border border-portal-border px-3 py-1 text-xs text-portal-ink-muted hover:border-portal-accent hover:text-portal-ink"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}

          <form
            className="flex items-center gap-2 border-t border-portal-border px-4 py-3"
            onSubmit={(event) => {
              event.preventDefault();
              void send(input);
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={intl.formatMessage({
                id: hasFormActions ? 'core.mobile.copilot.placeholder' : 'copilot.input.placeholder',
              })}
              disabled={busy}
              className="min-w-0 flex-1 rounded border border-portal-border bg-transparent px-3 py-2 text-sm text-portal-ink placeholder:text-portal-ink-muted focus:border-portal-accent focus:outline-none"
            />
            <button
              type="submit"
              disabled={busy || input.trim().length === 0}
              aria-label={intl.formatMessage({ id: 'copilot.button.send' })}
              className="btn-primary flex h-9 w-9 items-center justify-center rounded disabled:opacity-50"
            >
              <i className="fa fa-paper-plane" aria-hidden="true" />
            </button>
          </form>

          {budgetNearlyExhausted || conversationTokens !== null ? (
            <div className="space-y-0.5 px-4 pb-3 text-xs">
              {budgetNearlyExhausted ? (
                <p role="status" className="flex items-center gap-1 text-warning-text">
                  <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                  <span>{intl.formatMessage({ id: 'copilot.budget.nearlyExhausted' })}</span>
                </p>
              ) : null}
              {conversationTokens !== null ? (
                <p className="text-portal-ink-muted">
                  {intl.formatMessage(
                    { id: 'copilot.usage.conversation' },
                    { count: conversationTokens, tokens: formatTokens(conversationTokens, localeOf(intl)) },
                  )}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default CopilotDrawer;
